import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { JavascriptHookCustomAction, JavascriptHookType } from 'autogen/swagger/all';
import { AcceptMarketingCookiesToSeeElementBody, ChatNotAllowed } from 'autogen/translation-keys/trans-website-shared';
import CookieInfobox from 'components/CookieInfobox';
import useHasInformationCookieBeenAccepted from 'hooks/user/useHasInformationCookieBeenAccepted';
import { CookieCategory } from 'modules/helpers/cookieInformationHelper';
import websiteSettings from 'modules/websiteSettings';
import { zIndex } from 'variables';
import { CustomActionButton } from '../../shared/styles';
import type { CustomActionProps } from '../../shared/types';

const ButtonWrapper = styled.div`
    position: relative;
    display: inline-block;
`;

const StyledCookieInfobox = styled(CookieInfobox)`
    position: absolute;
    top: ${({ theme }) => `calc(100% - ${theme.Spacing4})`};
    left: 50%;
    transform: translateX(-50%);
    z-index: ${zIndex.modal};
`;

type CustomerServiceActionProps = {
    action: Omit<JavascriptHookCustomAction, 'javascriptHookType'> & {
        javascriptHookType: Extract<JavascriptHookType, 'OpenCustomerServiceChat'>;
    };
} & CustomActionProps;

const CustomerServiceAction = forwardRef<HTMLButtonElement, CustomerServiceActionProps>(
    ({ action, onClick, ...restProps }, ref) => {
        const { isZowieEnabled } = websiteSettings.liveChat;
        const isMarketingCookieAccepted = useHasInformationCookieBeenAccepted(CookieCategory.marketing);
        const { t } = useTranslation();
        const [showCookieInfobox, setShowCookieInfobox] = useState(false);

        if (isZowieEnabled && !isMarketingCookieAccepted) {
            return (
                <ButtonWrapper>
                    {showCookieInfobox && (
                        <StyledCookieInfobox
                            headline={t(ChatNotAllowed)}
                            body={t(AcceptMarketingCookiesToSeeElementBody)}
                            callback={() => {
                                setShowCookieInfobox(false);
                            }}
                        />
                    )}
                    <CustomActionButton
                        disabled={showCookieInfobox}
                        ref={ref}
                        type="button"
                        onClick={async (e) => {
                            if (isZowieEnabled && !isMarketingCookieAccepted) {
                                setShowCookieInfobox(true);
                                return;
                            }
                        }}
                        {...restProps}
                    />
                </ButtonWrapper>
            );
        }

        return (
            <CustomActionButton
                ref={ref}
                type="button"
                onClick={async (e) => {
                    if (onClick) {
                        onClick(e);
                    }

                    if (isZowieEnabled) window.Zowie.open(); // open Zowie chat
                }}
                {...restProps}
            />
        );
    }
);

export default CustomerServiceAction;
